import React from 'react';
import { useCalendar, useCalendarCell, useCalendarGrid, useLocale } from 'react-aria';
import { CalendarState, CalendarStateOptions, useCalendarState } from 'react-stately';
import { CalendarDate, createCalendar, DateValue, getWeeksInMonth } from '@internationalized/date';
import Button from '../button';
import { colors } from '../colors';
import { borderRadius } from '../tokens';
import ChevronLeftIcon from '../../../images/svg/chevron-left.svg';
import ChevronRightIcon from '../../../images/svg/chevron-right.svg';

const CalendarCell = ({ state, date }: { state: CalendarState, date: CalendarDate }): JSX.Element => {
    const ref = React.useRef(null);
    const {
        cellProps,
        buttonProps,
        isSelected,
        isOutsideVisibleRange,
        isDisabled,
        isUnavailable,
        formattedDate,
    } = useCalendarCell({ date }, state, ref);

    return (
        <td {...cellProps}>
            <div
                {...buttonProps}
                ref={ref}
                hidden={isOutsideVisibleRange}
                css={{
                    color: (() => {
                        if (isSelected) {
                            return colors.shadesWhite;
                        }
                        if (isDisabled) {
                            return colors.grey200;
                        }
                        return undefined;
                    })(),
                    display: 'flex',
                    justifyContent: 'center',
                    cursor: isDisabled ? 'not-allowed' : '',
                    outline: 'none',
                    backgroundColor: isSelected ? colors.primaryOrange : 'transparent',
                    borderRadius: borderRadius.small,
                    padding: '8px',
                    '@media (hover: hover)': {
                        '&:focus': {
                            backgroundColor: isSelected ? '' : colors.grey300,
                        },
                    },
                }}
                className={`cell ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''
                } ${isUnavailable ? 'unavailable' : ''}`}
            >
                {formattedDate}
            </div>
        </td>
    );
};
const CalendarGrid = ({ state, ...props }: { state: CalendarState }): JSX.Element => {
    const { locale } = useLocale();
    const { gridProps, headerProps, weekDays } = useCalendarGrid(props, state);

    // Get the number of weeks in the month so we can render the proper number of rows.
    const weeksInMonth = getWeeksInMonth(state.visibleRange.start, locale);

    return (
        <table {...gridProps} css={{ marginTop: 12 }}>
            <thead {...headerProps}>
                <tr>
                    {weekDays.map(day => <th css={{ fontWeight: '600' }} key={day}>{day}</th>)}
                </tr>
            </thead>
            <tbody>
                {[...new Array(weeksInMonth).keys()].map(weekIndex => (
                    <tr key={weekIndex} css={{ outline: 'none' }}>
                        {state.getDatesInWeek(weekIndex).map((date: CalendarDate|null) => {
                            // eslint-disable-next-line jsx-a11y/control-has-associated-label
                            if (!date) return <td />;
                            return (
                                <CalendarCell
                                    key={date.day}
                                    state={state}
                                    date={date}
                                />
                            );
                        })}
                    </tr>
                ))}
            </tbody>
        </table>
    );
};
const Calendar = (props: Partial<CalendarStateOptions<DateValue>>): JSX.Element => {
    const { locale } = useLocale();
    const state = useCalendarState({
        ...props,
        locale,
        createCalendar,
    });

    const { calendarProps, prevButtonProps, nextButtonProps, title } = useCalendar(
        props,
        state,
    );

    return (
        <div
            {...calendarProps}
            css={{
                overflow: 'hidden',
                color: colors.shadesBlack,
                borderRadius: 6,
                zIndex: 10,
                padding: '12px 16px',
                '.header': {
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingBottom: 12,
                    borderBottom: `1px solid ${colors.statesFocus}`,
                    svg: {
                        height: '26px',
                    },

                },
            }}
        >
            <div className="header">

                <Button
                    {...prevButtonProps}
                    onClick={() => state.focusPreviousPage()}
                    disabled={prevButtonProps.isDisabled}
                >
                    <ChevronLeftIcon
                        css={{ stroke: prevButtonProps.isDisabled ? colors.grey300 : colors.primaryOrange }}
                    />
                </Button>
                <h2>{title}</h2>
                <Button
                    {...nextButtonProps}
                    disabled={nextButtonProps.isDisabled}
                    onClick={() => state.focusNextPage()}
                >
                    <ChevronRightIcon
                        css={{ stroke: nextButtonProps.isDisabled ? colors.grey300 : colors.primaryOrange }}
                    />
                </Button>
            </div>
            <CalendarGrid state={state} />
        </div>
    );
};

export default Calendar;
